import { Component, ChangeDetectionStrategy, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { CsIconModule } from '@components/icon';
import { ButtonCtaComponent } from '@components/button-cta/button-cta.component';
import { ConnectionService } from '@services/connection-service';
import { BroadcastService } from '@services/broadcast-service';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { EventLoggerService } from '@services/event-logger-service';
import { RegimenService } from '@services/regimen-service';
import { ShiprocketService } from '@services/shiprocket-service';
import { CheckoutParams, CheckoutService } from '@services/checkout-service';
import { ExperimentService } from '@services/experiment/experiment.service';
import { Regimen, User } from '@cureskin/api-client/src/server-api';
import { AddressBookI } from '@store/modals';
import { fromActions } from '@store/actions';
import { fromSelectors } from '@store/selectors';
import { RegimenDetailsComponent } from './regimen-details/regimen-details.component';
import { ToPayComponent, PricingDetails } from '@shared/to-pay/to-pay.component';
import { RegimenPriceDetailsData } from '@shared/bottom-sheet-layouts/price-details-bottomsheet/price-details-bottomsheet.component';
import { PRICE_DETAILS_BOTTOMSHEET_TYPE, SHIPROCKET_FILL_ADDRESS_ACTION, ShiprocketFillAddressDismissResult } from '../app.constants';
import { UserService } from '@services/user/user.service';
import { LoadingModule } from '@components/loading';
import { DeliveryAddressBarComponent } from '@shared/delivery-address-bar/delivery-address-bar.component';
import { ExpectedDeliveryDateComponent } from '@shared/expected-delivery-date/expected-delivery-date.component';
import { ShiprocketFillAddressBottomsheetComponent } from './shiprocket-fill-address-bottomsheet/shiprocket-fill-address-bottomsheet.component';
import { RegimenPricingExperiment } from '@services/experiment/experiment.service.types';
import { AppConfig } from '../app.config';

@Component({
  selector: 'app-review-order',
  imports: [
    CommonModule,
    CsIconModule,
    ButtonCtaComponent,
    RegimenDetailsComponent,
    ToPayComponent,
    LoadingModule,
    DeliveryAddressBarComponent,
    ExpectedDeliveryDateComponent,
  ],
  templateUrl: './review-order.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReviewOrderComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly connectionService = inject(ConnectionService);
  private readonly broadcast = inject(BroadcastService);
  private readonly appBridgeService = inject(AppWebBridgeService);
  private readonly eventLoggerService = inject(EventLoggerService);
  private readonly regimenService = inject(RegimenService);
  private readonly userService = inject(UserService);
  private readonly store = inject(Store);
  private readonly bottomSheet = inject(MatBottomSheet);
  private readonly shiprocketService = inject(ShiprocketService);
  private readonly checkoutService = inject(CheckoutService);
  private readonly experimentService = inject(ExperimentService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly appConfig = inject(AppConfig);

  checkoutInProgress$: Observable<boolean> = this.store.select(fromSelectors.selectCheckoutProgress);

  user = signal<User | null>(null);
  regimen = signal<Regimen | null>(null);
  loading = signal<boolean>(true);
  defaultAddress = signal<AddressBookI | null>(null);
  shiprocketAddressExists = signal<boolean>(false);

  hasDefaultAddress = computed(() => !!this.defaultAddress()?.objectId);
  expectedDeliveryDate = signal<{ from?: Date; to?: Date }>({});
  orderDeliveryDateToCurrentDateDifference = signal<number | undefined>(undefined);
  newRegimenPricingExperiment: RegimenPricingExperiment;
  shiprocketCheckDone = false;
  checkoutPageOpenLogged = false;

  regimenPricing = computed(() => {
    const regimen = this.regimen();
    if (!regimen) return null;
    const fixedPriceMrp = regimen?.fixedPriceMRP || 0;
    const fixedPrice = regimen?.fixedPrice || 0;
    return { fixedPriceMrp, fixedPrice };
  });

  pricingDetails = computed<PricingDetails | null>(() => {
    const pricing = this.regimenPricing();
    if (!pricing) return null;
    return {
      totalMrp: pricing.fixedPriceMrp,
      totalSP: pricing.fixedPrice,
      discountInRupees: pricing.fixedPriceMrp - pricing.fixedPrice,
    };
  });

  bottomsheetData = computed<RegimenPriceDetailsData | null>(() => {
    const pricing = this.regimenPricing();
    if (!pricing) return null;
    return {
      type: PRICE_DETAILS_BOTTOMSHEET_TYPE.REGIMEN,
      fixedPriceMrp: pricing.fixedPriceMrp,
      fixedPrice: pricing.fixedPrice,
    };
  });

  async ngOnInit(): Promise<void> {
    this.loading.set(true);
    this.appBridgeService.setStatusBarColor('cureBlack');
    this.user.set(this.userService.getUser());
    this.sendOrderLogs();
    const regimenId = this.route.snapshot.queryParams?.regimenId;

    this.getUserExperiments();
    await this.getRegimen(regimenId);
    this.fetchDefaultAddress();
    this.loading.set(false);
  }

  getUserExperiments() {
    this.newRegimenPricingExperiment = this.experimentService.newRegimenPricingExperiment;
  }
  private async getRegimen(regimenId: string): Promise<void> {
    const selectedRegimen = await this.regimenService.getRegimenByRegimenId(regimenId);
    if (selectedRegimen) {
      this.regimen.set(selectedRegimen);
    }
  }

  private fetchDefaultAddress(): void {
    this.store.dispatch(fromActions.AddressBookDefaultFetchBegin({ skipRedirectOnMissing: true }));

    this.store.select(fromSelectors.selectDefaultAddress).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((address: AddressBookI) => {
      this.defaultAddress.set(address || null);
      this.logReviewOrderPageOpen(!!address?.zipCode);
      this.shouldCheckShipRocketAddress(address);
      if (address?.zipCode) {
        this.fetchDeliveryDate(address.zipCode.toString());
      } else {
        const pincode = this.user()?.get('lastLocationPincode');
        if (pincode) {
          this.fetchDeliveryDate(pincode.toString());
        }
      }
    });
  }

  private async fetchDeliveryDate(pincode: string): Promise<void> {
    const result = await this.checkoutService.fetchDeliveryDateInfo(
      pincode,
      this.user()?.get('languagePreference'),
    );
    this.expectedDeliveryDate.set(result.expectedDeliveryDate);
    this.orderDeliveryDateToCurrentDateDifference.set(result.dayDifference);
  }

  shouldCheckShipRocketAddress(address: AddressBookI | null): void {
    if (address || this.shiprocketCheckDone) {
      return;
    }

    this.shiprocketCheckDone = true;
    this.checkShiprocketAddress();
  }

  private async checkShiprocketAddress(): Promise<void> {
    const canProceed = this.experimentService.prefillAddressShiprocketEnabled || this.shiprocketService.isAcquisitionUser();
    if(!canProceed) {
      return;
    }

    try {
      const response = await this.connectionService.checkIfUserAddressExistsInShipRocket({
        countryCode: '91',
        phone: this.user().get('MobileNumber'),
      });
      this.shiprocketAddressExists.set(response?.exists ?? false);
      if (response?.exists) {
        this.eventLoggerService.cleverTapEvent('shiprocket_address_found', JSON.stringify({ userId: this.user()?.id }));
      }
    } catch {
      this.shiprocketAddressExists.set(false);
    }
  }

  back(): void {
    this.broadcast.broadcast('NAVIGATION_BACK');
  }

  navigateToAddresses(): void {
    this.eventLoggerService.cleverTapEvent('click', JSON.stringify({ name: 'review_order_change_address' }));
    const params = this.checkoutService.buildPreservedAddressParams(
      this.route.snapshot.queryParams,
      { fromCheckout: 'true', fromReviewOrder: 'true' },
    );
    this.connectionService.navigateToURL(this.checkoutService.buildUrlWithParams('/user/addresses', params));
  }

  handleCheckout(): void {
    if (this.hasDefaultAddress()) {
      this.makePayment();
      return;
    }

    if (this.shiprocketAddressExists()) {
      this.openShiprocketFillAddressBottomsheet();
      return;
    }

    this.navigateToAddressEdit();
  }

  private async makePayment(): Promise<void> {
    const queryParams = this.route.snapshot.queryParams;
    this.eventLoggerService.cleverTapEvent('click', JSON.stringify({ name: 'review_order_make_payment' }), [
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.MOENGAGE,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
    this.checkoutService.sendInitialOrderEvent();

    this.checkoutService.clearShiprocketStateIfNeeded(queryParams);
    const checkoutParams = this.getCheckoutParams(queryParams);
    const payload = await this.checkoutService.buildCheckoutPayloadForOrder(checkoutParams);
    this.checkoutService.dispatchCheckoutBegin(payload);
  }

  getCheckoutParams(queryParams): CheckoutParams {
    return {
      user: this.user(),
      typeOfOrder: queryParams.type,
      regimenId: queryParams.regimenId,
      products: queryParams.products,
      messageId: queryParams.messageId,
      services: queryParams.services,
      useCureSkinCash: queryParams.useCureSkinCash === 'true',
    };
  }

  private openShiprocketFillAddressBottomsheet(): void {
    const ref = this.bottomSheet.open(ShiprocketFillAddressBottomsheetComponent);
    ref.afterDismissed().subscribe((result: ShiprocketFillAddressDismissResult) => {
      if (!result) return;
      if (result.action === SHIPROCKET_FILL_ADDRESS_ACTION.Manual) {
        this.navigateToAddressEdit();
      } else if (result.action === SHIPROCKET_FILL_ADDRESS_ACTION.Shiprocket) {
        this.openShiprocketOtpFlow();
      }
    });
  }

  private openShiprocketOtpFlow(): void {
    this.shiprocketService.openOtpBottomsheet(
      this.bottomSheet,
      this.user().get('MobileNumber'),
      (verifiedPhone: string) => this.checkoutService.navigateToShiprocketAddressList(
        verifiedPhone,
        this.route.snapshot.queryParams,
        { fromCheckout: 'true', fromReviewOrder: 'true' },
      ),
    );
  }

  private sendOrderLogs(): void {
    this.eventLoggerService.trackEvent('checkout_entry', { username: this.user()?.get('username') });
    this.eventLoggerService.trackInElasticSearch({
      username: this.user()?.get('username'),
      added: new Date(),
      type: 'webApp',
      message: 'User into checkout page',
      event: 'CHECKOUT_ENTRY',
    });
  }

  private logReviewOrderPageOpen(defaultAddressExists: boolean): void {
    if (this.checkoutPageOpenLogged) return;
    this.checkoutPageOpenLogged = true;
    const regimenId = this.route.snapshot.queryParams?.regimenId;
    this.eventLoggerService.cleverTapEvent('pageOpen', JSON.stringify({
      regimenId,
      pageName: 'ReviewOrderPage',
      defaultAddressExists,
    }), [
      this.appConfig.Shared.EventTracker.MIXPANEL,
      this.appConfig.Shared.EventTracker.AMPLITUDE,
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
  }

  private navigateToAddressEdit(): void {
    this.eventLoggerService.cleverTapEvent('click', JSON.stringify({ name: 'review_order_add_address' }));
    const params = this.checkoutService.buildPreservedAddressParams(
      this.route.snapshot.queryParams,
      { fromCheckout: 'true', fromReviewOrder: 'true' },
    );
    this.connectionService.navigateToURL(this.checkoutService.buildUrlWithParams('/user/address/new', params));
  }
}
